import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { VESSELS } from "../data/site";

export default function VesselDetail() {
  const { slug } = useParams();
  const vessel = VESSELS.find((v) => v.slug === slug);
  if (!vessel) return <Navigate to="/fleet" replace />;

  const idx = VESSELS.findIndex((v) => v.slug === slug);
  const next = VESSELS[(idx + 1) % VESSELS.length];

  return (
    <div data-testid={`vessel-detail-${vessel.slug}`} className="bg-[#f7f4ec]">
      {/* Header */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 pt-36 md:pt-44 pb-10">
        <Link
          to="/fleet"
          data-testid="vessel-back-link"
          className="inline-flex items-center gap-2 nav-link"
        >
          <ArrowLeft size={14} /> The Fleet
        </Link>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 mt-8 md:mt-12 items-end">
          <div className="md:col-span-8">
            <p className="overline">Vessel</p>
            <h1
              className="font-serif text-5xl md:text-6xl lg:text-7xl text-[#13263c] leading-[0.98] tracking-tight mt-5 rise-in"
              data-testid="vessel-detail-name"
            >
              {vessel.name}
            </h1>
            <hr className="accent-rule mt-4" />
            <p
              className="mt-6 text-lg md:text-xl text-[#4a4a4a] max-w-xl rise-in rise-delay-1"
              data-testid="vessel-detail-line"
            >
              {vessel.line}
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <p className="overline text-[#b8895a]">Rate</p>
            <p
              className="font-serif text-3xl md:text-4xl text-[#13263c] mt-2"
              data-testid="vessel-detail-rate"
            >
              {vessel.rate}
            </p>
          </div>
        </div>
      </section>

      {/* Image */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="img-frame aspect-[16/8] rounded-md rise-in rise-delay-2">
          <img src={vessel.image} alt={vessel.name} />
        </div>
      </section>

      {/* Body */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14">
        <div className="md:col-span-7">
          <p className="overline">Description</p>
          <p
            className="font-serif text-2xl md:text-3xl text-[#2b2b2b] mt-5 leading-snug max-w-2xl"
            data-testid="vessel-detail-description"
          >
            {vessel.description}
          </p>
        </div>
        <aside className="md:col-span-5 space-y-8">
          <div className="surface-card rounded-md overflow-hidden">
            <p className="overline px-5 pt-5">Specifications</p>
            <table className="w-full text-sm mt-2">
              <tbody>
                {vessel.specs.map((s, i) => (
                  <tr
                    key={s.label}
                    className={i % 2 === 0 ? "bg-[#fbf8f1]" : "bg-[#f3ecdc]"}
                  >
                    <td className="py-2.5 px-5 text-[#4a4a4a]">{s.label}</td>
                    <td className="py-2.5 px-5 text-right text-[#2b2b2b] font-medium">{s.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {vessel.requirements && vessel.requirements.length > 0 && (
            <div data-testid="vessel-detail-requirements">
              <p className="overline text-[#b8895a]">Key Requirements</p>
              <ul className="mt-4 space-y-3">
                {vessel.requirements.map((r, i) => (
                  <li key={i} className="flex gap-3 text-[#2b2b2b]">
                    <span className="text-[#b8895a]">—</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {vessel.notes && (
            <div data-testid="vessel-detail-notes">
              <p className="overline">Notes</p>
              <p className="mt-3 text-[#4a4a4a] text-sm leading-relaxed">{vessel.notes}</p>
            </div>
          )}
          <Link
            to="/book"
            data-testid="vessel-detail-book-now"
            className="btn-primary"
          >
            Reserve this vessel
          </Link>
        </aside>
      </section>

      {/* Next vessel */}
      <section className="bg-[#efe9da] py-16 md:py-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <p className="overline">Next in the Fleet</p>
              <Link
                to={`/fleet/${next.slug}`}
                data-testid="vessel-next-link"
                className="font-serif text-4xl md:text-5xl text-[#13263c] mt-3 block hover:text-[#b8895a] transition-colors"
              >
                {next.shortName}
              </Link>
              <p className="mt-3 text-[#4a4a4a] max-w-md">{next.line}</p>
            </div>
            <Link to={`/fleet/${next.slug}`} className="link-underline">
              View vessel <ArrowRight size={14} className="inline ml-1" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
